/**
 * Built-in LLM models per provider and helper to merge with custom model IDs.
 * OpenAI list aligned with docs/OpenAI-refs/recommended-ai-models-wrtiting-research.md.
 * Cost tiers: low = cheap, medium = moderate, high = costly.
 */

import type { AIProvider } from './cms';

export type ModelCostTier = 'low' | 'medium' | 'high';

export interface ModelOption {
  label: string;
  value: string;
  /** Cost tier for admin UI: low = cheap, medium = moderate, high = costly */
  costTier?: ModelCostTier;
  /** Short use case for admin UI (e.g. "Web research", "Article writing") */
  useCase?: string;
  /** Per 1M tokens, USD (March 2026). Shown in Model guide table. */
  costPerMInput?: string;
  costPerMCached?: string;
  costPerMOutput?: string;
}

/**
 * Model IDs allowed in the OpenAI project (Model usage → Allowed models).
 * Only these work unless you add more in the OpenAI dashboard.
 */
export const ALLOWED_OPENAI_MODEL_IDS: string[] = [
  'gpt-4o-search-preview',
  'gpt-4o-search-preview-2025-03-11',
  'gpt-4o-mini-search-preview',
  'gpt-4o-mini-search-preview-2025-03-11',
  'gpt-5.4-pro',
  'gpt-5-mini',
  'gpt-4o-mini',
  'o4-mini-deep-research',
  // Codex models for coding (CMS page coding, agentic tasks)
  'gpt-5.1-codex-mini',
  'gpt-5.1-codex-max',
  'gpt-5.2-codex',
  'gpt-5.3-codex',
  'gpt-5.1-codex',
];

/** Per 1M tokens USD (March 2026). Input / Cached / Output. */
function costPerM(i: string, o: string, c?: string): { costPerMInput: string; costPerMCached?: string; costPerMOutput: string } {
  return c ? { costPerMInput: i, costPerMCached: c, costPerMOutput: o } : { costPerMInput: i, costPerMOutput: o };
}

export const BUILTIN_MODELS: Record<AIProvider, ModelOption[]> = {
  openai: [
    // Research & writing (use dated IDs; undated gpt-4o-search-preview often returns "Model not found")
    { label: 'GPT-4o Search', value: 'gpt-4o-search-preview-2025-03-11', costTier: 'medium', useCase: 'Web research, citations', ...costPerM('2.50', '10.00') },
    { label: 'GPT-4o mini Search', value: 'gpt-4o-mini-search-preview-2025-03-11', costTier: 'low', useCase: 'Web research, cheaper', ...costPerM('0.15', '0.60') },
    { label: 'GPT-5.4 Pro', value: 'gpt-5.4-pro', costTier: 'high', useCase: 'Article writing, highest quality', ...costPerM('30', '180') },
    { label: 'GPT-5 mini (recommended)', value: 'gpt-5-mini', costTier: 'low', useCase: 'Bulk generation, SEO, cheap', ...costPerM('0.25', '2', '0.025') },
    { label: 'GPT-4o mini', value: 'gpt-4o-mini', costTier: 'low', useCase: 'General, cheap, widely available', ...costPerM('0.15', '0.60', '0.075') },
    { label: 'o4-mini deep research', value: 'o4-mini-deep-research', costTier: 'medium', useCase: 'Research, fact-check', ...costPerM('2', '8', '0.50') },
    // Codex — coding / CMS pages
    { label: 'GPT-5.3 Codex', value: 'gpt-5.3-codex', costTier: 'high', useCase: 'Coding, CMS pages, agentic', ...costPerM('1.75', '14', '0.175') },
    { label: 'GPT-5.2 Codex', value: 'gpt-5.2-codex', costTier: 'high', useCase: 'Coding, CMS pages, agentic', ...costPerM('1.75', '14', '0.175') },
    { label: 'GPT-5.1 Codex Max', value: 'gpt-5.1-codex-max', costTier: 'medium', useCase: 'Coding, long-horizon tasks', ...costPerM('1.25', '10', '0.125') },
    { label: 'GPT-5.1 Codex', value: 'gpt-5.1-codex', costTier: 'medium', useCase: 'Coding, CMS pages', ...costPerM('1.25', '10', '0.125') },
    { label: 'GPT-5.1 Codex mini', value: 'gpt-5.1-codex-mini', costTier: 'low', useCase: 'Coding, cost-effective', ...costPerM('0.25', '2', '0.025') },
  ],
  anthropic: [
    { label: 'Claude Opus 4', value: 'claude-opus-4-5', costTier: 'high', useCase: 'Best quality', ...costPerM('5', '25') },
    { label: 'Claude Sonnet 4 (recommended)', value: 'claude-sonnet-4-5', costTier: 'medium', useCase: 'Balance quality/cost', ...costPerM('3', '15') },
    { label: 'Claude Haiku 3.5', value: 'claude-haiku-3-5', costTier: 'low', useCase: 'Fast, cheap', ...costPerM('0.80', '4') },
  ],
  gemini: [
    { label: 'Gemini 2.0 Flash', value: 'gemini-2.0-flash', costTier: 'low', useCase: 'Fast, cheap' },
    { label: 'Gemini 1.5 Pro', value: 'gemini-1.5-pro', costTier: 'medium', useCase: 'General' },
    { label: 'Gemini 1.5 Flash', value: 'gemini-1.5-flash', costTier: 'low', useCase: 'Cheap' },
  ],
};

/** True if this OpenAI model ID is in the project's allowed list. */
export function isAllowedOpenAIModel(modelId: string): boolean {
  return ALLOWED_OPENAI_MODEL_IDS.includes(modelId);
}

/** Human-readable cost tier for admin UI. */
export function getCostTierLabel(tier: ModelCostTier): string {
  switch (tier) {
    case 'low':
      return 'Cheap';
    case 'medium':
      return 'Moderate';
    case 'high':
      return 'Costly';
    default:
      return '';
  }
}

/**
 * Return merged list: built-in models for the provider plus custom model IDs as "Custom: <id>".
 */
export function getModelsForProvider(
  provider: AIProvider,
  customModelIds: string[] = []
): ModelOption[] {
  const builtin = BUILTIN_MODELS[provider] ?? [];
  const custom = customModelIds
    .filter((id) => id.trim() && !builtin.some((m) => m.value === id.trim()))
    .map((id) => ({ label: `Custom: ${id}`, value: id.trim() }));
  return [...builtin, ...custom];
}
